
import React from 'react';
import { ShoppingItem, CustomShoppingItem } from '@/types/shoppingList';
import MobileShoppingItem from './MobileShoppingItem';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ShoppingListByCategoryProps {
  categories: {
    [category: string]: ShoppingItem[];
  };
  customItems?: {
    [category: string]: CustomShoppingItem[];
  };
  onItemCheckedChange: (category: string, itemId: string, checked: boolean) => void;
  onCustomItemCheckedChange?: (itemId: string, checked: boolean) => void;
  onRemoveCustomItem?: (itemId: string) => void;
  shoppingMode?: boolean;
}

const ShoppingListByCategory: React.FC<ShoppingListByCategoryProps> = ({
  categories,
  customItems = {},
  onItemCheckedChange,
  onCustomItemCheckedChange,
  onRemoveCustomItem,
  shoppingMode = false,
}) => {
  // Merge categories from both sources
  const allCategories = new Set([...Object.keys(categories), ...Object.keys(customItems)]);
  
  const [openCategories, setOpenCategories] = React.useState<Set<string>>(
    new Set(allCategories)
  );

  // Update open categories when new categories are added
  React.useEffect(() => {
    setOpenCategories(prev => {
      const updated = new Set(prev);
      allCategories.forEach(cat => updated.add(cat));
      return updated;
    });
  }, [categories, customItems]);

  const toggleCategory = (category: string) => {
    const newOpen = new Set(openCategories);
    if (newOpen.has(category)) {
      newOpen.delete(category);
    } else {
      newOpen.add(category);
    }
    setOpenCategories(newOpen);
  };

  // Sort categories alphabetically
  const sortedCategories = Array.from(allCategories).sort();

  return (
    <div className="space-y-4">
      {sortedCategories.map((category) => {
        const recipeItems = categories[category] || [];
        const customCategoryItems = customItems[category] || [];
        const allItems = [...recipeItems, ...customCategoryItems];
        
        if (allItems.length === 0) return null;
        
        const isOpen = openCategories.has(category);
        const checkedCount = allItems.filter(item => item.checked).length;
        const totalCount = allItems.length;

        return (
          <Collapsible key={category} open={isOpen} onOpenChange={() => toggleCategory(category)}>
            <CollapsibleTrigger className="w-full">
              <div className={cn(
                "flex items-center justify-between p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors",
                shoppingMode && "sticky top-0 z-10 shadow-sm border-b-2 border-gray-200"
              )}>
                <div className="flex items-center gap-3">
                  {isOpen ? (
                    <ChevronDown className="h-5 w-5 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-600" />
                  )}
                  <h4 className={cn(
                    "font-semibold text-left",
                    shoppingMode ? "text-xl" : "text-lg"
                  )}>{category}</h4>
                </div>
                <div className="flex items-center gap-3">
                  <span className={cn(
                    "text-gray-600 font-medium",
                    shoppingMode ? "text-base" : "text-sm"
                  )}>
                    {checkedCount}/{totalCount}
                  </span>
                  {checkedCount === totalCount && totalCount > 0 && (
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  )}
                </div>
              </div>
            </CollapsibleTrigger>
            
            <CollapsibleContent className="mt-3">
              <div className={cn(
                "space-y-3",
                shoppingMode && "px-2"
              )}>
                {/* Recipe items first */}
                {recipeItems.map((item) => (
                  <MobileShoppingItem
                    key={item.id}
                    item={item}
                    onCheckedChange={(checked) => onItemCheckedChange(category, item.id, checked)}
                  />
                ))}
                
                {/* Custom items with visual distinction */}
                {customCategoryItems.map((item) => (
                  <MobileShoppingItem
                    key={`custom-${item.id}`}
                    item={{
                      id: item.id,
                      name: item.name,
                      quantity: item.quantity || '',
                      checked: item.checked,
                      note: item.note,
                    }}
                    isCustomItem
                    onCheckedChange={(checked) => onCustomItemCheckedChange?.(item.id, checked)}
                    onRemove={() => onRemoveCustomItem?.(item.id)}
                  />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
};

export default ShoppingListByCategory;
